import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getUserToken, getUserBrandId } from '@/redux/user';
import {
    useGetBrandDefaultPricingPolicyQuery,
    useGetBrandWhitelistedDomainNamesQuery,
    useGetBrandIsWhitelistingEnabledQuery,
    useUpdateBrandDefaultPricingPolicyMutation,
    useUpdateBrandWhitelistedDomainNamesMutation,
    useUpdateBrandWhitelistingEnabledMutation,
} from '@/services/innovorder/brand/brand.endpoints';
import { useGetGuestGroupsQuery } from '@/services/innovorder/guestGroup/guestGroup.endpoints';
import { DefaultPricingPolicy, WhitelistedDomainName } from '@/services/innovorder/brand/brand.type';
import { Option } from '@/components/atoms/Select';
import Loader from '@/components/atoms/Loader';

import Select from '@/components/atoms/Select';
import { Switch } from '@/components/atoms/Switch';
import { computeText } from '@/locales/utils';
import {
    Container,
    SectionTitle,
    SectionDescription,
    Row,
    TableContainer,
    Table,
    DomainInput,
    WhitelistingSection,
    WhitelistingInfo,
    SwitchContainer,
    SaveButton,
    DeleteButton,
} from './PricingPolicy.style';

const PricingPolicy: React.FunctionComponent = () => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const brandId = useSelector(getUserBrandId);

    // State for form data
    const [defaultPricingPolicy, setDefaultPricingPolicy] = useState<DefaultPricingPolicy>({
        entranceFeeId: null,
        pricingRuleId: null,
        group: null,
    });
    const [whitelistedDomains, setWhitelistedDomains] = useState<WhitelistedDomainName[]>([
        { emailDomainName: '', entranceFeeId: null, pricingRuleId: null, group: null },
    ]);
    const [isWhitelistingEnabled, setIsWhitelistingEnabled] = useState(false);

    // Track selected group for filtering pricing rules
    const [selectedGroupForDefault, setSelectedGroupForDefault] = useState<string | null>(null);
    const [selectedGroupsForDomains, setSelectedGroupsForDomains] = useState<Map<number, string | null>>(new Map());

    // API hooks for loading data
    const { data: guestGroupsData, isLoading: isLoadingGuestGroups } = useGetGuestGroupsQuery(
        { brandId: brandId!, token, depth: 0 },
        { skip: !brandId || !token, refetchOnMountOrArgChange: true },
    );
    const { data: defaultPolicyData, isLoading: isLoadingDefaultPolicy } = useGetBrandDefaultPricingPolicyQuery(
        { brandId: brandId!, token },
        { skip: !brandId || !token, refetchOnMountOrArgChange: true },
    );
    const { data: whitelistedDomainsData, isLoading: isLoadingWhitelistedDomains } =
        useGetBrandWhitelistedDomainNamesQuery(
            { brandId: brandId!, token },
            { skip: !brandId || !token, refetchOnMountOrArgChange: true },
        );
    const { data: isWhitelistingEnabledData, isLoading: isLoadingWhitelistingEnabled } =
        useGetBrandIsWhitelistingEnabledQuery(
            { brandId: brandId!, token },
            { skip: !brandId || !token, refetchOnMountOrArgChange: true },
        );

    // Mutation hooks for saving
    const [updateDefaultPricingPolicy, { isLoading: isSavingDefaultPolicy }] =
        useUpdateBrandDefaultPricingPolicyMutation();
    const [updateWhitelistedDomains, { isLoading: isSavingDomains }] = useUpdateBrandWhitelistedDomainNamesMutation();
    const [updateWhitelistingEnabled, { isLoading: isSavingWhitelisting }] =
        useUpdateBrandWhitelistingEnabledMutation();

    const isSaving = isSavingDefaultPolicy || isSavingDomains || isSavingWhitelisting;

    // Helper function to get the first pricing rule ID for a group
    const getFirstPricingRuleIdForGroup = useCallback(
        (groupName: string | null): number | null => {
            if (!groupName || !guestGroupsData) {
                return null;
            }

            const selectedGuestGroup = guestGroupsData.find((g) => g.name === groupName);
            if (!selectedGuestGroup?.pricingRules || selectedGuestGroup.pricingRules.length === 0) {
                return null;
            }

            return selectedGuestGroup.pricingRules[0].pricingRuleId;
        },
        [guestGroupsData],
    );

    // Memoize groups options from guest groups data
    const groups = useMemo(() => {
        if (!guestGroupsData) return [];

        const groupsWithNone: Option<string | null>[] = [
            { value: null, label: computeText(intl, 'customer.pricingPolicy.noGroup') },
            ...guestGroupsData.map((group) => ({ value: group.name, label: group.name })),
        ];
        return groupsWithNone;
    }, [guestGroupsData, intl]);

    // Memoize pricing rules for the default policy based on selected group
    const pricingRulesForDefault = useMemo(() => {
        if (!selectedGroupForDefault || !guestGroupsData) {
            return [];
        }

        const selectedGuestGroup = guestGroupsData.find((g) => g.name === selectedGroupForDefault);
        if (!selectedGuestGroup || !selectedGuestGroup.pricingRules || selectedGuestGroup.pricingRules.length === 0) {
            return [];
        }

        return selectedGuestGroup.pricingRules.map((rule) => ({
            value: rule.pricingRuleId,
            label: rule.code,
        }));
    }, [selectedGroupForDefault, guestGroupsData]);

    // Function to get pricing rules for a specific group (for domain rows)
    const getPricingRulesForGroup = useCallback(
        (groupName: string | null): Option<number | null>[] => {
            if (!groupName || !guestGroupsData) {
                return [];
            }

            const selectedGuestGroup = guestGroupsData.find((g) => g.name === groupName);
            if (
                !selectedGuestGroup ||
                !selectedGuestGroup.pricingRules ||
                selectedGuestGroup.pricingRules.length === 0
            ) {
                return [];
            }

            return selectedGuestGroup.pricingRules.map((rule) => ({
                value: rule.pricingRuleId,
                label: rule.code,
            }));
        },
        [guestGroupsData, intl],
    );

    // Load default pricing policy
    useEffect(() => {
        if (defaultPolicyData) {
            setDefaultPricingPolicy({
                entranceFeeId: defaultPolicyData.entranceFeeId || null,
                pricingRuleId: defaultPolicyData.pricingRuleId || null,
                group: defaultPolicyData.group || null,
            });
            // Set the selected group for filtering pricing rules
            setSelectedGroupForDefault(defaultPolicyData.group || null);
        }
    }, [defaultPolicyData]);

    // Load whitelisted domains
    useEffect(() => {
        if (whitelistedDomainsData) {
            const domains =
                whitelistedDomainsData.length > 0
                    ? [
                          ...whitelistedDomainsData,
                          { emailDomainName: '', entranceFeeId: null, pricingRuleId: null, group: null },
                      ]
                    : [{ emailDomainName: '', entranceFeeId: null, pricingRuleId: null, group: null }];
            setWhitelistedDomains(domains);

            // Initialize selected groups for each domain
            const groupsMap = new Map<number, string | null>();
            whitelistedDomainsData.forEach((domain, index) => {
                groupsMap.set(index, domain.group || null);
            });
            setSelectedGroupsForDomains(groupsMap);
        }
    }, [whitelistedDomainsData]);

    // Load whitelisting enabled status
    useEffect(() => {
        if (isWhitelistingEnabledData !== undefined) {
            setIsWhitelistingEnabled(isWhitelistingEnabledData);
        }
    }, [isWhitelistingEnabledData]);

    // Handler functions
    const addEmptyDomainEntryIfNeeded = useCallback(() => {
        const hasEmptyEntry = whitelistedDomains.some((domain) => domain.emailDomainName === '');
        if (!hasEmptyEntry) {
            setWhitelistedDomains((prev) => [
                ...prev,
                { emailDomainName: '', entranceFeeId: null, pricingRuleId: null, group: null },
            ]);
        }
    }, [whitelistedDomains]);

    const updateDomainName = (index: number, emailDomainName: string) => {
        setWhitelistedDomains((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], emailDomainName };
            return updated;
        });

        if (emailDomainName !== '') {
            addEmptyDomainEntryIfNeeded();
        }
    };

    const updateDomainField = (
        index: number,
        field: keyof Omit<WhitelistedDomainName, 'emailDomainName'>,
        value: number | string | null,
    ) => {
        setWhitelistedDomains((prev) => {
            const updated = [...prev];
            if (field === 'group') {
                const groupValue = value as string | null;
                updated[index] = { ...updated[index], [field]: groupValue };

                // Update the selected group for this domain to filter pricing rules
                setSelectedGroupsForDomains((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(index, groupValue);
                    return newMap;
                });

                // Auto-select first pricing rule for the selected group
                updated[index].pricingRuleId = getFirstPricingRuleIdForGroup(groupValue);
            } else {
                updated[index] = { ...updated[index], [field]: value };
            }
            return updated;
        });
    };

    const deleteDomain = (index: number) => {
        // Update domains array
        setWhitelistedDomains((prev) => prev.filter((_, i) => i !== index));

        // Rebuild the Map with sequential indices based on the filtered array
        setSelectedGroupsForDomains(() => {
            const newMap = new Map<number, string | null>();
            whitelistedDomains
                .filter((_, i) => i !== index)
                .forEach((domain, newIndex) => {
                    newMap.set(newIndex, domain.group);
                });
            return newMap;
        });
    };

    const updateDefaultPolicyField = (field: keyof DefaultPricingPolicy, value: number | string | null) => {
        if (field === 'group') {
            const groupValue = value as string | null;
            setSelectedGroupForDefault(groupValue);

            setDefaultPricingPolicy((prev) => ({
                ...prev,
                [field]: groupValue,
                // Auto-select first pricing rule when group changes
                pricingRuleId: getFirstPricingRuleIdForGroup(groupValue),
            }));
        } else {
            setDefaultPricingPolicy((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleSave = async () => {
        if (!brandId || !token) {
            console.error('Cannot save: missing brandId or token');
            return;
        }

        try {
            // Filter out empty domain entries
            const nonEmptyDomains = whitelistedDomains
                .filter((d) => d.emailDomainName !== '')
                .map((d) => ({
                    emailDomainName: d.emailDomainName,
                    entranceFeeId: d.entranceFeeId,
                    pricingRuleId: d.pricingRuleId,
                    group: d.group,
                }));

            // Execute all three API calls in parallel
            await Promise.all([
                updateDefaultPricingPolicy({
                    brandId,
                    token,
                    defaultPricingPolicy,
                }).unwrap(),
                updateWhitelistedDomains({
                    brandId,
                    token,
                    domainNames: nonEmptyDomains,
                }).unwrap(),
                updateWhitelistingEnabled({
                    brandId,
                    token,
                    isEnabled: isWhitelistingEnabled,
                }).unwrap(),
            ]);
        } catch (error) {
            console.error('Error saving pricing policy:', error);
        }
    };

    if (isLoadingGuestGroups || isLoadingDefaultPolicy || isLoadingWhitelistedDomains || isLoadingWhitelistingEnabled) {
        return <Loader withContainer />;
    }

    return (
        <Container>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <SectionTitle>{computeText(intl, 'customer.pricingPolicy.defaultTitle')}</SectionTitle>
                <SectionDescription>
                    {computeText(intl, 'customer.pricingPolicy.defaultDescription')}
                </SectionDescription>

                <Row>
                    <div>
                        <Select
                            labelId="customer.pricingPolicy.group"
                            value={defaultPricingPolicy.group}
                            options={groups}
                            onChange={(value) => updateDefaultPolicyField('group', value)}
                        />
                    </div>

                    <div>
                        <Select
                            labelId="customer.pricingPolicy.pricingPolicy"
                            value={defaultPricingPolicy.pricingRuleId}
                            options={pricingRulesForDefault}
                            onChange={(value) => updateDefaultPolicyField('pricingRuleId', value)}
                            disabled={!selectedGroupForDefault}
                        />
                    </div>
                </Row>

                <SectionTitle>{computeText(intl, 'customer.pricingPolicy.customTitle')}</SectionTitle>

                <TableContainer>
                    <Table>
                        <thead>
                            <tr>
                                <th>{computeText(intl, 'customer.pricingPolicy.domainName')}</th>
                                <th>{computeText(intl, 'customer.pricingPolicy.group')}</th>
                                <th>{computeText(intl, 'customer.pricingPolicy.pricingPolicy')}</th>
                                <th className="action-column"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {whitelistedDomains.map((domain, index) => {
                                const selectedGroupForThisDomain = selectedGroupsForDomains.get(index) || null;
                                const pricingRulesForThisDomain = getPricingRulesForGroup(selectedGroupForThisDomain);

                                return (
                                    <tr key={index}>
                                        <td className="domain-name-email">
                                            @
                                            <DomainInput
                                                type="text"
                                                placeholder="mail.fr"
                                                pattern="[a-z0-9\.\-]+\.[a-z]{2,20}"
                                                value={domain.emailDomainName}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    updateDomainName(index, e.target.value)
                                                }
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                value={domain.group}
                                                options={groups}
                                                onChange={(value) => updateDomainField(index, 'group', value)}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                value={domain.pricingRuleId}
                                                options={pricingRulesForThisDomain}
                                                onChange={(value) => updateDomainField(index, 'pricingRuleId', value)}
                                                disabled={!selectedGroupForThisDomain}
                                            />
                                        </td>
                                        <td>
                                            {domain.emailDomainName && (
                                                <DeleteButton
                                                    type="button"
                                                    onClick={() => deleteDomain(index)}
                                                    title={computeText(intl, 'customer.pricingPolicy.delete')}
                                                >
                                                    ×
                                                </DeleteButton>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </TableContainer>

                <WhitelistingSection>
                    <WhitelistingInfo>
                        {computeText(intl, 'customer.pricingPolicy.enableWhitelistingInfo')}
                    </WhitelistingInfo>
                    <SwitchContainer>
                        <Switch value={isWhitelistingEnabled} onChange={setIsWhitelistingEnabled} />
                        <span>{computeText(intl, 'customer.pricingPolicy.enableWhitelisting')}</span>
                    </SwitchContainer>
                </WhitelistingSection>

                <SaveButton type="submit" disabled={isSaving}>
                    {isSaving ? 'Saving...' : computeText(intl, 'customer.pricingPolicy.save')}
                </SaveButton>
            </form>
        </Container>
    );
};

export default PricingPolicy;
